import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import GraphicLung from '../GraphicLung/GraphicLung';

import ChevronIcon from '../../assets/svgs/ChevronIcon';

import roundToPrecision from '../../utils/roundToPrecision';
import translate from '../../libs/i18n';
import { reportAttenuationDefaultProps } from '../../utils/defaultProps';
import { reportAttenuationShape } from '../../utils/propTypesShapes';
import { useWindowSize } from '../../utils/useWindowSize';
import { breakpoints } from '../../ui/variables';

import { ReportAttenuationStyle } from './ReportAttenuationStyle';

const ReportAttenuation = ({ attenuation, section, setSection }) => {
  const language = useSelector((state) => state.settings.language);
  const windowSize = useWindowSize();

  const [isWxgaPlus, setIsWxgaPlus] = useState(true);
  // const [section, setSection] = useState('');
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (windowSize.width >= breakpoints.wxgaPlus.width && !isWxgaPlus) {
      setIsWxgaPlus(true);
    } else if (windowSize.width < breakpoints.wxgaPlus.width && isWxgaPlus) {
      setIsWxgaPlus(false);
    }
  }, [windowSize, isWxgaPlus]);

  const handleNavigation = (position) => {
    let currentSlide = slide + position;

    if (currentSlide < 0) {
      currentSlide = 0;
    }

    if (currentSlide > 2) {
      currentSlide = 2;
    }

    if (currentSlide !== slide) {
      setSlide(currentSlide);
    }
  };


  const renderGraphicLungWrapper = (
    title,
    percent,
    classification,
    ll,
    lu,
    rl,
    rm,
    ru
  ) => (
    <div className="slide-content-card">
      <div className="graphic-lung-wrapper">
        <GraphicLung
          classification={classification}
          mouseEvents={{
            ru: { over: () => setSection(`ru-${classification}`) },
            rm: { over: () => setSection(`rm-${classification}`) },
            rl: { over: () => setSection(`rl-${classification}`) },
            lu: { over: () => setSection(`lu-${classification}`) },
            ll: { over: () => setSection(`ll-${classification}`) },
            leave: () => setSection(''),
          }}
        />
        <p
          className={`section-info right-upper${
            section === `ru-${classification}` ? ' show' : ''
          }`}
        >
          <span className="label">RU</span>
          <span className="value">{`${
            ru ? roundToPrecision(ru * 100, 1) : 0
          }%`}</span>
        </p>
        <p
          className={`section-info right-middle${
            section === `rm-${classification}` ? ' show' : ''
          }`}
        >
          <span className="label">RM</span>
          <span className="value">{`${
            rm ? roundToPrecision(rm * 100, 1) : 0
          }%`}</span>
        </p>
        <p
          className={`section-info right-lower${
            section === `rl-${classification}` ? ' show' : ''
          }`}
        >
          <span className="label">RL</span>
          <span className="value">{`${
            rl ? roundToPrecision(rl * 100, 1) : 0
          }%`}</span>
        </p>
        <p
          className={`section-info left-upper${
            section === `lu-${classification}` ? ' show' : ''
          }`}
        >
          <span className="label">LU</span>
          <span className="value">{`${
            lu ? roundToPrecision(lu * 100, 1) : 0
          }%`}</span>
        </p>
        <p
          className={`section-info left-lower${
            section === `ll-${classification}` ? ' show' : ''
          }`}
        >
          <span className="label">LL</span>
          <span className="value">{`${
            ll ? roundToPrecision(ll * 100, 1) : 0
          }%`}</span>
        </p>
      </div>
      {title && title.length ? (
        <h3 className="slide-title">{translate(title, language)}</h3>
      ) : null}
      <p className="percentage-data">
        <span className="percentage-value">{`${
          percent ? roundToPrecision(percent * 100, 1) : 0
        }%`}</span>
      </p>
    </div>
  );

  return (
    <ReportAttenuationStyle>
      <div
        className={`carousel-component-${isWxgaPlus ? 'disabled' : 'active'}`}
      >
        <div
          className="carousel-slide low-attenuation"
          style={{ transform: `translateX(-${100 * slide}%)` }}
        >
          {attenuation && attenuation.low
            ? renderGraphicLungWrapper(
                'lowAttenuation',
                attenuation.low.total,
                'low',
                attenuation.low.ll,
                attenuation.low.lu,
                attenuation.low.rl,
                attenuation.low.rm,
                attenuation.low.ru
              )
            : null}
        </div>
        <div
          className="carousel-slide normal-attenuation"
          style={{ transform: `translateX(-${100 * slide}%)` }}
        >
          {attenuation && attenuation.normal
            ? renderGraphicLungWrapper(
                'normalAttenuation',
                attenuation.normal.total,
                'normal',
                attenuation.normal.ll,
                attenuation.normal.lu,
                attenuation.normal.rl,
                attenuation.normal.rm,
                attenuation.normal.ru
              )
            : null}
        </div>
        <div
          className="carousel-slide high-attenuation"
          style={{ transform: `translateX(-${100 * slide}%)` }}
        >
          {attenuation && attenuation.high
            ? renderGraphicLungWrapper(
                'highAttenuation',
                attenuation.high.total,
                'high',
                attenuation.high.ll,
                attenuation.high.lu,
                attenuation.high.rl,
                attenuation.high.rm,
                attenuation.high.ru
              )
            : null}
        </div>
      </div>
      <button
        type="button"
        className={`slide-navigation previous-slide${
          slide <= 0 ? ' disabled' : ''
        }${isWxgaPlus ? ' hide' : ''}`}
        onClick={() => handleNavigation(-1)}
      >
        <ChevronIcon />
      </button>
      <button
        type="button"
        className={`slide-navigation next-slide${
          slide >= 2 ? ' disabled' : ''
        }${isWxgaPlus ? ' hide' : ''}`}
        onClick={() => handleNavigation(1)}
      >
        <ChevronIcon />
      </button>
    </ReportAttenuationStyle>
  );
};

ReportAttenuation.propTypes = {
  attenuation: reportAttenuationShape,
  section: PropTypes.string,
  setSection: PropTypes.func.isRequired,
};

ReportAttenuation.defaultProps = {
  attenuation: reportAttenuationDefaultProps,
  section: '',
};

export default ReportAttenuation;
